import { Reveal } from "@/components/motion";
import Icon from "@/components/Icon";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * ModuleSpotlight — reusable marketing section that pairs a headline+copy+bullets
 * with a real product screenshot. Used to distribute screenshots contextually
 * (Command Center, Board, Sales/CRM, Business Data, etc.) rather than grouping
 * them in a single gallery.
 */
export default function ModuleSpotlight({
  eyebrow,
  title,
  description,
  bullets = [],
  bulletsTitle,
  screenshot,
  screenshotAlt,
  ctaLabel,
  ctaTo,
  reverse = false,
  accent = "text-brand-teal",
  chip,
  testid,
}) {
  return (
    <section className="relative border-t border-border py-20 sm:py-28" data-testid={testid || "module-spotlight"}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <Reveal>
            {eyebrow && <p className={`eyebrow ${accent}`} data-testid="ms-eyebrow">{eyebrow}</p>}
            <h2 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem]">{title}</h2>
            {description && (
              <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">{description}</p>
            )}
            {chip && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground">
                {chip}
              </div>
            )}
            {bullets.length > 0 && (
              <div className="mt-6 rounded-2xl border border-border bg-card/50 p-5">
                {bulletsTitle && <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{bulletsTitle}</p>}
                <ul className="grid gap-2.5 sm:grid-cols-2">
                  {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${accent.replace("text-", "bg-")}`} />
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ctaLabel && ctaTo && (
              <Link
                to={ctaTo}
                className="mt-6 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform"
                data-testid="ms-cta"
              >
                {ctaLabel} <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </Reveal>

          <Reveal delay={0.1}>
            <div className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-[0_30px_70px_-35px_rgba(0,0,0,0.5)]">
              <div className="flex items-center gap-1.5 border-b border-border bg-muted/40 px-4 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-brand-red/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-brand-orange/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-brand-green/60" />
                <span className="ml-3 truncate text-xs text-muted-foreground">{screenshotAlt}</span>
              </div>
              <img
                src={screenshot}
                alt={screenshotAlt}
                loading="lazy"
                className="block w-full transition-transform duration-700 group-hover:scale-[1.02]"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
